import classNames from 'classnames';
import React, { memo, Suspense, useCallback, useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';

import { Animation, AuthProtectedLink, LanguageSelect, SvgIconUrl, Tooltip } from 'common/components';

import { useBowser, useCustomer, useElementSizeVariable, useI18n, useTradePageLink } from 'common/hooks';
import svgIcons from 'common/svgIcons';

import AuthButtonsComponent from 'components/AuthButtons/AuthButtons.component';

import { routes } from 'routes/routes.constants';

import { useTheme } from 'blocks/ThemeProvider';

import animations from '../Animation/animations.constants';
import { UserMenu } from '../UserMenu';
import './Menu.css';
import { MenuItem } from './menu.types';

const Balance = React.lazy(
  () => import(/* webpackChunkName: "Balance" */ 'common/components/Balance/Balance.component'),
);

type MenuMode = {
  mode?: 'fixed' | 'static';
};

const Menu: React.FC<React.PropsWithChildren<MenuMode>> = React.memo(({ mode = 'fixed' }) => {
  const [hover, setHover] = useState<string | undefined>();
  const { isMobile, isDesktop } = useBowser();
  const { translate } = useI18n();
  const { checkFeaturesAvailability } = useCustomer();
  const { layout } = useTheme();
  const tradeLink = useTradePageLink();

  const { ref } = useElementSizeVariable('menu-height', mode === 'static');

  const isSpotAvailable = useMemo(() => checkFeaturesAvailability('spot'), [checkFeaturesAvailability]);
  const isFuturesAvailable = useMemo(() => checkFeaturesAvailability('derivatives'), [checkFeaturesAvailability]);
  const isExchangeAvailable = useMemo(() => checkFeaturesAvailability('otc'), [checkFeaturesAvailability]);
  const isEarnAvailable = useMemo(() => checkFeaturesAvailability('earn'), [checkFeaturesAvailability]);
  const isCryptoPayAvailable = useMemo(() => checkFeaturesAvailability('cryptoPay'), [checkFeaturesAvailability]);

  const items: MenuItem[] = useMemo(
    () => [
      {
        name: 'home',
        icon: 'menu-icon-home',
        isMobile: true,
        isDesktop: true,
        skipAuthCheck: true,
        url: routes.getHome(),
      },
      {
        name: 'exchange',
        icon: 'menu-icon-exchange',
        skipAuthCheck: true,
        isMobile: isExchangeAvailable,
        isDesktop: isExchangeAvailable,
        url: routes.getExchangeFormPage(),
      },
      {
        name: 'wallet',
        icon: 'menu-icon-wallet',
        isMobile: true,
        isDesktop: true,
        url: routes.getWalletPage(),
      },
      {
        name: 'earn',
        icon: 'menu-icon-earn',
        isMobile: isEarnAvailable,
        isDesktop: isEarnAvailable,
        skipAuthCheck: true,
        url: routes.getEarnListPage(),
      },
      {
        name: 'trade',
        icon: 'menu-icon-trade',
        isMobile: isFuturesAvailable || isSpotAvailable,
        isDesktop: isFuturesAvailable || isSpotAvailable,
        skipAuthCheck: true,
        url: tradeLink,
      },
      {
        name: 'crypto-pay',
        icon: 'menu-icon-crypto-pay',
        isMobile: false,
        isDesktop: isCryptoPayAvailable,
        url: routes.getCryptoPayPage(),
      },
      {
        name: 'margin',
        icon: 'menu-icon-margin',
        isMobile: false,
        isDesktop: false,
        url: routes.getMarginPage(),
      },
      {
        name: 'menu',
        icon: 'menu-icon-menu',
        isMobile: false,
        isDesktop: false,
        skipAuthCheck: true,
        url: routes.getMenuPage(),
      },
    ],
    [isExchangeAvailable, isEarnAvailable, isFuturesAvailable, isSpotAvailable, tradeLink, isCryptoPayAvailable],
  );

  const menuFilteredByPlatform = useMemo(() => items.filter((item) => (isMobile && isMobile === item.isMobile) || (isDesktop && isDesktop === item.isDesktop)), [isDesktop, isMobile, items]);

  const itemsCount = useMemo(() => menuFilteredByPlatform.length, [menuFilteredByPlatform.length]);

  const className = classNames({
    menu: true,
    'menu--fixed': mode === 'fixed',
  });

  const onRefreshClick = useCallback(() => {
    window.location.reload();
  }, []);

  const IsMatch = useRouteMatch;

  const hiddenRoutes = useMemo(
    () => [
      routes.getSpotAssetPage(),
      routes.getDerivativesAssetPage(),
      routes.getWithdrawalAddressPage(),
      routes.getReferalPage(),
      routes.getRewardsPage(),
      routes.getSecurityPage(),
      routes.getLoyaltyPage(),
      routes.getVerificationLevelPage(),
      routes.getChangePasswordPage(),
      routes.getCoinPage(),
      routes.getWireTransferPage(),
      routes.getTransferPage(),
      routes.getTransferChooseCurrencyPage(),
      routes.getExchangeChooseCurrencyPage(),
      routes.getDepositPage(),
      routes.getReceivePage(),
      routes.getEditLimitOrderPage(),
      routes.getMenuPage(),
      routes.getTokenPresalePage(),
      routes.getPaymentCardPage(),
    ],
    [],
  );

  const isHidden = useRouteMatch(hiddenRoutes) && isMobile;

  const onMouseLeave = useCallback(() => setHover(undefined), []);

  // Hide only on mobile view of specific routes
  if (isHidden) return null;

  const labelClassName = classNames({
    'menu__item-label': true,
    Body5_extasmall_reg_10: isMobile,
    Body2_reg_14: isDesktop,
  });

  const menuLeftClassNames = classNames('menu__left', {
    'menu__left--collapsed': layout.sidebar.compact,
  });

  return (
    <div ref={ref} data-test='menu' className={className}>
      {isDesktop && (
        <div className={menuLeftClassNames}>
          <Suspense>
            <Balance collapsed />
          </Suspense>
          <AuthButtonsComponent collapsed className='menu__left-buttons' />
        </div>
      )}
      <div className='menu__main' style={{ gridTemplateColumns: `repeat(${itemsCount}, 1fr)` }}>
        {menuFilteredByPlatform
          .map((item) => {
            const isActive = !!IsMatch(item.url);
            const isHover = isActive || item.name === hover;
            const menuItemClassName = classNames({
              menu__item: true,
              'menu__item--active': isActive,
            });
            const onMouseEnter = () => setHover(item.name);
            return (
              <AuthProtectedLink
                skipAuthCheck={item.skipAuthCheck}
                key={item.name}
                data-test={`menu-${item.name}`}
                to={item.url}
                className={menuItemClassName}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
              >
                <Animation
                  options={{
                    animationData: animations[item.icon],
                    autoplay: isHover,
                  }}
                  isStopped={!isHover}
                  width='24rem'
                  height='24rem'
                />
                <span className={labelClassName} data-test={`menu__${item.name}-label`}>
                  {translate(`mobile.bottomnavigation.${item.name}.title`)}
                </span>
              </AuthProtectedLink>
            );
          })}
      </div>
      {isDesktop && (
        <div className='menu__right'>
          <UserMenu />
          <div className='devider_vertical' />
          <LanguageSelect />
          {isDesktop && (
            <Tooltip enabled overlay={translate.toText('mobile:refresh:btn:label')} placement='topLeft'>
              <button type='button' className='btn-refresh' onClick={onRefreshClick}>
                <SvgIconUrl symbol={svgIcons.Refresh()} />
              </button>
            </Tooltip>
          )}
          {/* <div className='devider_vertical' />
          <ThemeSwitcher /> */}
        </div>
      )}
    </div>
  );
});

export default memo(Menu);
